import { useEffect, useRef, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import Modal from "../ui/Modal";
import ConfirmDialog from "../ui/ConfirmDialog";
import FormField, { FormActions, fieldInputStyle } from "../ui/FormField";
import {
  createDia,
  updateDia,
  deleteDia,
  fetchDiaPdfBlob,
} from "../../services/roteiroDiasService";
import RoteiroDiaDocumentosSection from "./RoteiroDiaDocumentosSection";

const EMPTY = {
  numeroDia: 1,
  titulo: "",
  atividades: "",
  transporte: "",
  hospedagem: "",
  refeicoes: "",
  notas: "",
};

// Modal de criação/edição de um RoteiroDia (dia do itinerário).
// Props:
//   open, onClose, onSaved, onDeleted — lifecycle
//   roteiroId — dono do dia
//   dia — se preenchido, modo edição (PUT + delete); senão criação (POST)
//   proximoNumero — sugestão de numeroDia para criação (baseado no count atual)
//   maxDias — limite = (dataFim - dataInicio) + 1 do roteiro
export default function RoteiroDiaFormModal({
  open,
  roteiroId,
  dia,
  proximoNumero = 1,
  maxDias = 99,
  onClose,
  onSaved,
  onDeleted,
}) {
  const { C } = useTheme();
  const isEdit = !!dia;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Snapshot do form ao abrir o modal — base pra detectar "dirty" sem
  // depender da prop `dia` (que pode ser referência mutável). Comparação
  // por JSON é suficiente: form só tem strings/números.
  const initialFormRef = useRef(EMPTY);
  const isDirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  useEffect(() => {
    if (!open) return;
    const next = dia
      ? {
          numeroDia: dia.numeroDia ?? 1,
          titulo: dia.titulo ?? "",
          atividades: dia.atividades ?? "",
          transporte: dia.transporte ?? "",
          hospedagem: dia.hospedagem ?? "",
          refeicoes: dia.refeicoes ?? "",
          notas: dia.notas ?? "",
        }
      : { ...EMPTY, numeroDia: proximoNumero };
    setForm(next);
    initialFormRef.current = next;
    setError(null);
  }, [open, dia, proximoNumero]);

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function close() {
    if (saving || deleting) return;
    onClose?.();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const numero = parseInt(form.numeroDia, 10);
    if (!Number.isFinite(numero) || numero < 1) {
      setError("Número do dia deve ser ≥ 1.");
      return;
    }
    if (numero > maxDias) {
      setError(`Número do dia deve estar entre 1 e ${maxDias}.`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        numeroDia: numero,
        titulo: form.titulo.trim() || null,
        atividades: form.atividades.trim() || null,
        transporte: form.transporte.trim() || null,
        hospedagem: form.hospedagem.trim() || null,
        refeicoes: form.refeicoes.trim() || null,
        notas: form.notas.trim() || null,
      };
      if (isEdit) {
        await updateDia(roteiroId, dia.id, payload);
      } else {
        await createDia(roteiroId, payload);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.errors?.join?.(" ") ||
          "Erro ao salvar dia."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handlePrint() {
    if (!isEdit || isDirty || printing) return;

    // CRÍTICO: abre a aba ANTES do await pra preservar o user gesture.
    // window.open chamado depois do fetch é tratado como popup
    // não-solicitado pelos browsers (Chrome/Firefox/Safari). Mesmo padrão
    // do "Visualizar PDF" do roteiro completo (RoteirosPage).
    const newTab = window.open("", "_blank");
    if (!newTab) {
      setError("Permita pop-ups pra visualizar o PDF.");
      return;
    }

    setPrinting(true);
    setError(null);
    try {
      const blob = await fetchDiaPdfBlob(roteiroId, dia.id);
      const url = window.URL.createObjectURL(blob);
      newTab.location.href = url;
      // Revoga após 60s — tempo suficiente pro browser renderizar o PDF.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      newTab.close();
      setError(err?.response?.data?.message || "Não foi possível gerar o PDF do dia.");
    } finally {
      setPrinting(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteDia(roteiroId, dia.id);
      setConfirmDelete(false);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao excluir dia.");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  const inputStyle = fieldInputStyle(C);

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Editar Dia ${dia?.numeroDia}` : "Adicionar Dia"}
        subtitle={isEdit ? dia?.titulo || "Dia do itinerário" : "Novo dia do roteiro"}
        width={620}
      >
        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "100px 1fr",
              gap: 14,
            }}
          >
            <FormField label="Dia nº" required hint={`1 a ${maxDias}`}>
              <input
                type="number"
                min="1"
                max={maxDias}
                value={form.numeroDia}
                onChange={(e) => update("numeroDia", e.target.value)}
                style={inputStyle}
                autoFocus
              />
            </FormField>

            <FormField label="Título">
              <input
                value={form.titulo}
                onChange={(e) => update("titulo", e.target.value)}
                style={inputStyle}
                placeholder="Ex: Chegada em Milão"
              />
            </FormField>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 14,
              marginTop: 14,
            }}
          >
            <FormField label="🎯 Atividades">
              <textarea
                value={form.atividades}
                onChange={(e) => update("atividades", e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }}
                placeholder="Ex: Transfer aeroporto → hotel, city tour à tarde"
              />
            </FormField>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <FormField label="🚗 Transporte">
                <input
                  value={form.transporte}
                  onChange={(e) => update("transporte", e.target.value)}
                  style={inputStyle}
                  placeholder="Transfer privado"
                />
              </FormField>

              <FormField label="🏨 Hospedagem">
                <input
                  value={form.hospedagem}
                  onChange={(e) => update("hospedagem", e.target.value)}
                  style={inputStyle}
                  placeholder="Hotel Principe di Savoia"
                />
              </FormField>
            </div>

            <FormField label="🍽️ Refeições">
              <input
                value={form.refeicoes}
                onChange={(e) => update("refeicoes", e.target.value)}
                style={inputStyle}
                placeholder="Jantar no Ristorante Cracco"
              />
            </FormField>

            <FormField label="📝 Notas">
              <textarea
                value={form.notas}
                onChange={(e) => update("notas", e.target.value)}
                rows={2}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }}
                placeholder="Informações adicionais, restrições, pontos de atenção..."
              />
            </FormField>
          </div>

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: C.redDim,
                color: C.red,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}

          <FormActions
            onCancel={close}
            onDelete={isEdit ? () => setConfirmDelete(true) : undefined}
            saving={saving}
            submitLabel={isEdit ? "Salvar dia" : "Adicionar dia"}
            onPrint={isEdit ? handlePrint : undefined}
            printLabel={printing ? "Carregando..." : "🖨️ Imprimir Dia"}
            printDisabled={!isEdit || isDirty || printing}
            printTitle={
              !isEdit
                ? "Salve o dia primeiro pra poder imprimir."
                : isDirty
                ? "Salve as alterações primeiro pra imprimir a versão atual."
                : "Abrir PDF deste dia em nova aba"
            }
          />
        </form>

        {/* Seção de documentos — só em modo edição (precisa do diaId no banco).
            Fora do <form> de propósito: upload/delete são independentes do
            "Salvar dia" e não afetam o isDirty dos campos do dia. */}
        {isEdit && dia?.id && (
          <RoteiroDiaDocumentosSection roteiroId={roteiroId} diaId={dia.id} />
        )}
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Excluir dia?"
        message={`Tem certeza que deseja excluir o Dia ${dia?.numeroDia}${
          dia?.titulo ? ` — ${dia.titulo}` : ""
        }?`}
        confirmLabel="Excluir"
        danger
        loading={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
